import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

// Services
import { postSetAttributeForRow, deleteRow } from './batchProcessingService';

// Utils
import { appendContexteToUrl } from '../../utils/url';

// Components
import useToast from '../../components/molecules/Toast/useToast';

export const BATCH_PROCESSING_QUERY_KEY = 'batchProcessing';

/**
 * @typedef {object} BatchSetAttributeOnProgressPayload
 * @property {number} completed - Number of rows processed for this action
 * @property {'running' | 'done'} [status]
 * @property {string} [message] - Message from API (success or error)
 * @property {boolean} [isError] - Whether this row failed
 * @property {ApiBatchSetAttributeOpenTabAction} [openTab] - Open tab action returned by API
 */

/**
 * @typedef {object} BatchSetAttributeVariables
 * @property {string} baseUrl - Base URL up to and including classId (setAttributeMultipleUrl)
 * @property {string[]} selectedRows - Selected row ids
 * @property {Array<{ id: string, value: any }>} actions - Actions (attribute id + value)
 * @property {string | null} [contexte] - Page contexte JSON for set-attribut query param
 * @property {(actionIndex: number, payload: BatchSetAttributeOnProgressPayload) => void} [onProgress] - Progress callback per action (keyed by index)
 */

/**
 * Batch set-attribute mutation: one POST per (row, action). Calls onProgress(actionIndex, { completed, status?, message? }) per action/row.
 * @param {import('@tanstack/react-query').UseMutationOptions<{ errorCount: number }, Error, BatchSetAttributeVariables, unknown>} [options = {}] - The mutation options
 * @returns {import('@tanstack/react-query').UseMutationResult<{ errorCount: number }, Error, BatchSetAttributeVariables, unknown>} The batch set-attribute mutation
 */
export const useBatchSetAttributeMutation = (options = {}) => {
  const { notify } = useToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async ({
      baseUrl, selectedRows, actions, contexte, onProgress,
    }) => {
      const totalOps = selectedRows.length * actions.length;
      let errorCount = 0;

      await actions.reduce(
        (prev, action, actionIndex) => prev.then(() => {
          onProgress?.(actionIndex, { completed: 0, status: 'running' });
          const completedForAction = { current: 0 };
          const totalForAction = selectedRows.length;

          const promises = selectedRows.map((rowId, rowIndex) => {
            const opIndex = actionIndex * selectedRows.length + rowIndex;
            const isFirst = opIndex === 0;
            const isLast = opIndex === totalOps - 1;
            return postSetAttributeForRow(
              appendContexteToUrl(`${baseUrl}/${rowId}/${action.id}`, contexte),
              { value: action.value, isFirstLoopOccurence: isFirst, isLastLoopOccurence: isLast },
            )
              .then((response) => ({
                message: response?.message ?? null,
                openTab: /** @type {ApiBatchSetAttributeOpenTabAction | null} */ (
                  /** @type {any} */ (response)?.openTab ?? null
                ),
                isError: false,
              }))
              .catch((err) => ({
                message: err?.response?.data?.message ?? err?.message ?? null,
                openTab: /** @type {ApiBatchSetAttributeOpenTabAction | null} */ (null),
                isError: true,
              }))
              .then(({ message, openTab, isError }) => {
                completedForAction.current += 1;
                if (isError) errorCount += 1;
                onProgress?.(actionIndex, {
                  completed: completedForAction.current,
                  isError,
                  ...(message ? { message: String(message) } : {}),
                  ...(openTab ? { openTab } : {}),
                });
              });
          });

          return Promise.all(promises).then(() => {
            onProgress?.(actionIndex, { completed: totalForAction, status: 'done' });
          });
        }),
        Promise.resolve(),
      );

      return { errorCount };
    },
    ...options,
    onSuccess: (data, variables, context) => {
      const hasErrors = data?.errorCount > 0;
      notify({
        message: hasErrors
          ? t('sidePanel.batchProcessingWithErrors')
          : t('sidePanel.batchProcessingSuccess'),
        type: hasErrors ? 'alert' : 'success',
      });
      options.onSuccess?.(data, variables, context);
    },
  });
};

/**
 * @typedef {object} DeleteMultipleVariables
 * @property {string[]} selectedRows - Selected row ids
 * @property {(payload: { completed: number, status?: 'running' | 'done', message?: string, isError?: boolean }) => void} [onProgress]
 */

/**
 * Delete rows one by one: POST ${url}/${rowId} for each selected row, with progress reporting.
 * @param {string} url - The base url for deletion
 * @param {import('@tanstack/react-query').UseMutationOptions<{ errorCount: number }, Error, DeleteMultipleVariables, unknown>} [options = {}] - The mutation options
 * @returns {import('@tanstack/react-query').UseMutationResult<{ errorCount: number }, Error, DeleteMultipleVariables, unknown>} The delete multiple url mutation
 */
export const useDeleteMultipleUrlMutation = (url, options = {}) => {
  const { notify } = useToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async ({ selectedRows, onProgress }) => {
      onProgress?.({ completed: 0, status: 'running' });
      let completed = 0;

      let errorCount = 0;

      const promises = selectedRows.map((rowId) => deleteRow(url, rowId)
        .then((response) => ({
          message: response?.message ?? null,
          isError: false,
        }))
        .catch((err) => ({
          message: err?.response?.data?.message ?? err?.message ?? null,
          isError: true,
        }))
        .then(({ message, isError }) => {
          completed += 1;
          if (isError) errorCount += 1;
          onProgress?.({
            completed,
            isError,
            ...(message ? { message: String(message) } : {}),
          });
        }));

      await Promise.all(promises);
      onProgress?.({ completed: selectedRows.length, status: 'done' });
      return { errorCount };
    },
    ...options,
    onSuccess: (data, variables, context) => {
      const hasErrors = data?.errorCount > 0;
      notify({
        message: hasErrors
          ? t('sidePanel.batchProcessingWithErrors')
          : t('sidePanel.batchProcessingSuccess'),
        type: hasErrors ? 'alert' : 'success',
      });
      options.onSuccess?.(data, variables, context);
    },
  });
};
